<template>
  <div class="flowbuilder-node-container" ref="containerEl">
    <BaseNode
      ref="nodeEl"
      :node="node"
      :icon="nodeIcon"
      :metrics="metrics"
      @click="emit('click')"
      @delete-node="emit('delete-node')"
      classes="bg-teal-100 dark:bg-teal-950 ring-teal-400/50 dark:ring-teal-700/50 does-terminate">
      <template #context>Message:</template>

      <div>{{ plaintextContent }}</div>
    </BaseNode>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, inject } from 'vue'
import BaseNode from './BaseNode.vue'
import { stripHtml } from '@rightmessage/shared-utils'
import { getNodeIcon } from '../../utils/nodeIcons'
import type { NodeMessage } from '../../types/nodes'

/**
 * MessageNode Component
 * 
 * Displays a message to the visitor and terminates the flow.
 * 
 * Features:
 * - Shows HTML content as plain text preview
 * - Terminal node (flow ends here)
 * - Supports rich HTML content that gets rendered to visitor
 * 
 * Props:
 * - node: The message node configuration with content
 * - icon: Optional custom icon
 * - CodeIcon: Default icon component for messages
 */

interface Props {
  node: NodeMessage
  icon?: any
  CodeIcon: any
}

const props = defineProps<Props>()

const emit = defineEmits(['delete-node', 'click'])

const containerEl = ref<HTMLElement | null>(null)
const nodeEl = ref<InstanceType<typeof BaseNode> | null>(null)

const getNodeMetrics = inject<(node: any) => any>('getNodeMetrics', () => null)

// Get the appropriate icon for this node
const nodeIcon = computed(() => {
  // If an icon is passed as a prop, use it
  if (props.icon) return props.icon
  
  // Otherwise use the mapped icon
  return getNodeIcon(props.node)
})

// Get metrics for this node
const metrics = computed(() => {
  return getNodeMetrics(props.node)
})

/**
 * Convert HTML content to plain text for preview
 * This gives a clean preview of what the message contains
 */
const plaintextContent = computed(() => {
  if (!props.node.content) return 'No message content'
  
  // Use stripHtml to remove HTML tags and get clean text
  return stripHtml(props.node.content) || 'No message content'
})
</script>